import { Link } from "react-router-dom"
import { ListPlus } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { KIND_LABEL_PT, type Kind } from "@/lib/kind-labels"
import { KIND_META } from "@/components/ProposalKindChip"
import { cn } from "@/lib/utils"

interface ModuleCardProps {
  kind: Kind
  /** Total de propostas não-arquivadas. Undefined = a carregar. */
  count?: number
  /** Path absoluto para o CTA "Novo orçamento". */
  ctaPath: string
  /** Se preenchido, mostra estado "Em breve — Fase X" e desactiva CTA. */
  comingSoonPhase?: number
  /** Path para "Ver todos" (filtrado por kind). Opcional. */
  viewAllPath?: string
}

export function ModuleCard({
  kind,
  count,
  ctaPath,
  comingSoonPhase,
  viewAllPath,
}: ModuleCardProps) {
  const meta = KIND_META[kind]
  const Icon = meta.Icon
  const label = KIND_LABEL_PT[kind]
  const disabled = comingSoonPhase != null

  return (
    <Card
      className={cn(
        "flex h-full flex-col transition-shadow",
        disabled ? "opacity-70" : "hover:shadow-md",
      )}
    >
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", meta.color)} />
          <h3 className="text-sm font-semibold tracking-tight">{label}</h3>
        </div>

        <div>
          <div className="text-3xl font-semibold tracking-tight">
            {count == null ? "—" : count}
          </div>
          <p className="text-xs text-muted-foreground">
            {disabled ? "Propostas (em preparação)" : "Propostas activas"}
          </p>
        </div>

        <div className="mt-auto flex flex-col gap-2">
          {disabled ? (
            <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
              Em breve — Fase {comingSoonPhase}
            </div>
          ) : (
            <>
              <Button asChild size="sm">
                <Link to={ctaPath}>
                  <ListPlus className="h-4 w-4" />
                  Novo orçamento
                </Link>
              </Button>
              {viewAllPath && (
                <Button asChild variant="ghost" size="sm">
                  <Link to={viewAllPath}>Ver todos</Link>
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
